// tuneAuthored — which tune paths a BLUEPRINT wrote, and how to keep them out
// of localStorage.
//
// The rule this serves is already stated on liveTuneStore.setStateEphemeral:
// blueprint-authored values must not "masquerade as the dev's own localStorage
// settings next load". The camera nodes honour it (setCameraFromBlueprint →
// ephemeral); the `loadEnvironment` node did NOT, and the consequence was
// visible within an hour of shipping it: a node loaded Basement_Level, the
// graph edit was never saved, and after a reload the character had NO
// loadEnvironment node anywhere — yet still booted into Basement_Level, out of
// `aos-livetune-v3`. The blueprint's choice had become the user's setting.
//
// Why not simply skip persisting? Because the stale value must be REMOVED, not
// just left un-updated. A user pick is persisted; if a blueprint later loads a
// different environment and we only skip the write, the next boot restores the
// user's older environment (a 50 MB download) and the blueprint then overrides
// it — two environments per load. So persist() strips the authored paths from
// what it stores, exactly as it already does for ephemeral camera keys.
//
// Split out of liveTuneStore.js so both halves are testable under plain
// `node --test`: that module reads window/localStorage at import time and its
// persist() is a no-op without a DOM, so the interesting logic would otherwise
// only be reachable through a browser.

/**
 * The dotted paths a patch writes, at slice or slice.key granularity.
 *
 * `{ environment: { splatUrl, pose } }` → `['environment.splatUrl', 'environment.pose']`
 * `{ lighting: {...} }`                 → one path per lighting key
 * `{ debug: true }`                     → `['debug']`
 *
 * Per-key rather than per-slice so an authored write to one key can't suppress
 * persistence of a sibling the user set themselves.
 */
export function authoredPathsIn(patch) {
  const out = []
  if (!patch || typeof patch !== 'object') return out
  for (const slice of Object.keys(patch)) {
    const v = patch[slice]
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const k of Object.keys(v)) out.push(`${slice}.${k}`)
    } else {
      out.push(slice)
    }
  }
  return out
}

function clone(v) {
  if (Array.isArray(v)) return v.map(clone)
  if (v && typeof v === 'object') {
    const o = {}
    for (const k of Object.keys(v)) o[k] = clone(v[k])
    return o
  }
  return v
}

/**
 * A copy of `state` with every path in `paths` removed — what gets written to
 * localStorage. `state` itself is never mutated: the live session keeps the
 * blueprint's values, only the persisted snapshot forgets them.
 *
 * A slice left empty by the strip is dropped entirely, so the stored blob says
 * "nothing here" rather than carrying an empty object that reads as a setting.
 */
export function stripPaths(state, paths) {
  if (!state || !paths || paths.length === 0) return state
  const out = clone(state)
  for (const p of paths) {
    const dot = p.indexOf('.')
    if (dot === -1) {
      delete out[p]
      continue
    }
    const slice = p.slice(0, dot)
    const key = p.slice(dot + 1)
    if (out[slice] && typeof out[slice] === 'object') {
      delete out[slice][key]
      if (Object.keys(out[slice]).length === 0) delete out[slice]
    }
  }
  return out
}
